import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDateDisplay(dateString: string, mode: "relative" | "message" | "absolute" = "relative"): string {
  const date = new Date(dateString);
  const now = new Date();

  if (mode === "absolute") {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  if (mode === "message") {
    const timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (date.toDateString() === now.toDateString()) return timeStr;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${timeStr}`;
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff < 7) {
      return `${date.toLocaleDateString([], { weekday: "short" })} ${timeStr}`;
    }
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  }

  // Relative mode (default)
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/** @deprecated Use formatDateDisplay(date, "relative") instead */
export function formatRelativeTime(dateString: string): string {
  return formatDateDisplay(dateString, "relative");
}

export function formatAction(action: string): string {
  return action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert simple markdown (bold, hr, unordered lists) to HTML.
 * Escapes HTML entities first to prevent injection.
 * Only intended for server-controlled policy text, not arbitrary user input.
 */
export function simpleMarkdownToHtml(md: string): string {
  const escaped = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const lines = escaped.split("\n");
  const result: string[] = [];
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      if (inList) { result.push("</ul>"); inList = false; }
      result.push("<hr />");
      continue;
    }

    // List item
    if (trimmed.startsWith("- ")) {
      if (!inList) { result.push("<ul>"); inList = true; }
      const content = trimmed.slice(2).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      result.push(`<li>${content}</li>`);
      continue;
    }

    // Close list if we left it
    if (inList) { result.push("</ul>"); inList = false; }

    // Empty line
    if (trimmed === "") {
      continue;
    }

    // Bold + paragraph
    const content = trimmed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    result.push(`<p>${content}</p>`);
  }

  if (inList) result.push("</ul>");
  return result.join("");
}
